import { appendPath, mutateContentValue } from '@truqc/content-path';
import { v4 as uuidV4, validate as uuidValidate } from 'uuid';
import { hash } from 'spark-md5';
import { parser } from '../../behaviors/src/lib/parser';
import { evaluateSource } from '../../behaviors/src/lib/evaluateSource';
import { getUser } from '../utils/paths';
import { buildLogPatch } from '../components/editor/commonActions/logValue';
import { convertToAbsolutePath } from '../components/editor/utils';
import store from '../reducers/storeRegistration';

/**
  * @function evalute - evaluate prepareNewFormsBehaviors
  *
  * Process an array of objects into content objects with specified contentPath mappings
  * (intended for new form creation in conjunction with addFormsBehavior)
  *
  * rawFormData:              CSV lines as objects in array. Object keys correspond to csv headers.
  * columnToContentPathMaps:  Object of CSV headers with the selection map values
  *                           (mappedContentPath) and selectedUnits (where applicable)
  */
export const evaluate = async (entity, entityName, source, content, parentContentPath, transientContext, nativeFunctions, sourceEvaluator) => {
  var _state$login, _state$login2;
  const rawFormData = await evaluateSource(source.rawFormData, content, parentContentPath, transientContext, nativeFunctions);
  const columnToContentPathMaps = await evaluateSource(source.columnToContentPathMaps, content, parentContentPath, transientContext, nativeFunctions);
  const [importFormType] = Object.keys(content.importFormType);
  const logContentPathResult = await sourceEvaluator({
    type: 'getLogContentPathBehavior',
    formTypeIdentifier: importFormType
  });
  const logContentPath = typeof logContentPathResult === 'string' ? logContentPathResult : undefined;
  const contentMap = {};
  Object.values(columnToContentPathMaps).forEach(({
    title: columnTitle,
    mappedContentPath,
    selectedUnits
  }) => {
    if (mappedContentPath && Object.values(mappedContentPath).length) {
      const {
        contentPath,
        fieldType,
        formatOptions,
        breadcrumbs
      } = Object.values(mappedContentPath)[0];
      contentMap[columnTitle] = {
        contentPath,
        fieldType,
        formatOptions,
        breadcrumbs
      };
      // if there are units selected, attach the 'toDefault'
      // formula in order to handle the conversion
      if (selectedUnits) {
        contentMap[columnTitle].conversionFormula = Object.values(selectedUnits)[0].toDefault;
      }
    }
  });
  if (Object.keys(contentMap).length === 0) {
    /* eslint-disable-next-line no-console */
    console.log('whoops! no content mappings selected!');
    // todo - return some kind of error or user feedback note to select mappings
    // (or add behavior to not be able to engage this behavior without at least one set of mappings)
  }
  const newForms = [];
  const wildcardUUIDs = {};
  const validationErrors = {};
  const {
    firstName,
    lastName
  } = getUser();
  const currentUser = `${firstName} ${lastName}`;
  const state = store.getState();
  const latitude = (state === null || state === void 0 || (_state$login = state.login) === null || _state$login === void 0 || (_state$login = _state$login.geolocation) === null || _state$login === void 0 ? void 0 : _state$login.latitude) || undefined;
  const longitude = (state === null || state === void 0 || (_state$login2 = state.login) === null || _state$login2 === void 0 || (_state$login2 = _state$login2.geolocation) === null || _state$login2 === void 0 ? void 0 : _state$login2.longitude) || undefined;
  function addValidationError(column, row, type, message) {
    if (!validationErrors[column]) {
      validationErrors[column] = {};
    }
    const errorId = uuidV4();
    validationErrors[column][errorId] = {
      row,
      errorType: type,
      message
    };
  }
  rawFormData.forEach((row, index) => {
    const rowIndex = index + 2;
    let created = 0;
    const newForm = {};
    Object.keys(row).forEach(columnTitle => {
      var _contentMap$columnTit;
      if ((_contentMap$columnTit = contentMap[columnTitle]) !== null && _contentMap$columnTit !== void 0 && _contentMap$columnTit.contentPath) {
        let valuePassedValidation = true;
        const {
          contentPath,
          fieldType,
          formatOptions,
          conversionFormula,
          breadcrumbs
        } = contentMap[columnTitle];
        const processedPath = contentPath.split('/').map((segment, wildcardIndex) => {
          if (segment === '*') {
            // Generate a UUID for the wildcard position if it doesn't exist
            wildcardUUIDs[wildcardIndex] = wildcardUUIDs[wildcardIndex] || uuidV4();
            return wildcardUUIDs[wildcardIndex];
          }
          return segment;
        }).join('/');
        const title = row[columnTitle];

        // Validate UUID fieldType values. This column doesn't come from the csvImport tags,
        // we add them to allow users to update existing forms from the csv file.
        // If the user maps a column to a uuid fieldType and the value is not a valid UUID,
        // we add a validation error.
        if (title && fieldType === 'uuid' && !uuidValidate(title)) {
          valuePassedValidation = false;
          addValidationError(columnTitle, rowIndex, 'Invalid UUID', `${columnTitle} requires a valid UUID but '${title}' is not a valid UUID.`);
        }
        let value = title;
        const displayValue = title;
        // ignore empty values - this means the csv had no entry filled in
        if (!value && fieldType !== 'uuid') {
          valuePassedValidation = false;
          addValidationError(columnTitle, rowIndex, 'Empty value', `${columnTitle} is required but was empty.`);
        } else if (fieldType === 'formRef') {
          // this is only tested with Jobs
          // assumption is that the saveFormsBehavior can expand using the title
          value = {
            _formRef: title,
            _atomic: true
          };
        } else if (fieldType === 'picker') {
          const formatOptionsArray = Array.isArray(formatOptions) ? formatOptions : [];
          const lookup = {};
          formatOptionsArray.forEach(opt => {
            const optTitle = (opt.title || '').trim().toLowerCase();
            if (optTitle) {
              lookup[optTitle] = opt.identifier;
            }
          });
          value = {
            _atomic: true
          };
          // Seems like a reasonable assumption that picker csv values will be
          // assumed to be seperate options if there are commas
          title.split(',').forEach(option => {
            const trimmedOption = option.trim();
            if (trimmedOption && trimmedOption.length) {
              const loweredOption = trimmedOption.toLowerCase();
              if (lookup[loweredOption]) {
                value[lookup[loweredOption]] = {
                  title: option,
                  _created: created
                };
              } else {
                value[hash(trimmedOption)] = {
                  title: option,
                  _created: created
                };
              }
              created += 1;
            }
          });
        } else if (fieldType === 'number') {
          const parsedValue = parseFloat(title);
          const invalidNumberRegex = /[^0-9.-]+/;
          if (invalidNumberRegex.test(title) || Number.isNaN(parsedValue)) {
            valuePassedValidation = false;
            addValidationError(columnTitle, rowIndex, 'Invalid number', `${columnTitle} requires a number input but '${title}' is not a valid number.`);
          } else {
            value = conversionFormula ? parser.evaluate(conversionFormula, {
              value: parsedValue
            }) : parsedValue;
          }
        }
        if (fieldType !== 'uuid' && Boolean(logContentPath)) {
          const logPatch = buildLogPatch(currentUser, {
            value,
            displayValue
          }, processedPath, breadcrumbs, latitude, longitude);
          const logPath = convertToAbsolutePath(logContentPath, processedPath);
          const fullLogPath = appendPath(logPath, uuidV4());
          mutateContentValue(fullLogPath, newForm, logPatch);
        }
        if (valuePassedValidation) {
          created += 1;
          mutateContentValue(processedPath, newForm, value);
        }
      }
    });
    if (Object.values(newForm).length) newForms.push(newForm);
  });
  return {
    forms: newForms,
    validation: validationErrors
  };
};
export default {
  evaluate
};